// Crossref REST API client.
//
// Endpoint: https://api.crossref.org/works/{doi}
// Polite-pool: pass `mailto` query param (set from user settings, optional).
//
// Retraction detection: a retraction notice is a separate Crossref work of
// type "retraction" with `update-to` pointing back at the retracted DOI. The
// retracted work's own metadata exposes this via its `update-to` relations
// when crawled from the retraction side, and via `relation.retraction` /
// `update-policy` on some entries. We rely on `update-to` plus the
// `is-retracted`-like signal in `subtype`/`type` where present.

import { fetchJson } from './http';
import type { CrossrefWork, Result } from './types';

const BASE = 'https://api.crossref.org/works';

// Crossref's "polite pool" gives far better rate limits and far fewer 429s
// (which surface to the browser as opaque CORS failures). It only needs a
// stable contact identifier — not user data. Use the project's no-reply
// address by default; a user-configured mailto overrides it.
const DEFAULT_MAILTO = 'manuscriptskim@users.noreply.github.com';

export interface CrossrefOptions {
  /** Overrides the default polite-pool contact. Optional. */
  mailto?: string;
}

export function crossrefWorkUrl(doi: string, opts: CrossrefOptions = {}): string {
  const url = new URL(`${BASE}/${encodeURIComponent(doi)}`);
  url.searchParams.set('mailto', opts.mailto || DEFAULT_MAILTO);
  return url.toString();
}

interface CrossrefMessage {
  DOI?: string;
  title?: string[];
  author?: Array<{ given?: string; family?: string }>;
  'container-title'?: string[];
  type?: string;
  subtype?: string;
  published?: { 'date-parts'?: number[][] };
  issued?: { 'date-parts'?: number[][] };
  abstract?: string;
  'update-to'?: Array<{ DOI?: string; type?: string; label?: string }>;
  relation?: Record<string, Array<{ 'id-type'?: string; id?: string; 'asserted-by'?: string }>>;
}

function stripAbstractMarkup(raw: string | undefined): string | null {
  if (!raw) return null;
  // Crossref abstracts often come as JATS XML fragments. Strip tags and collapse
  // whitespace. Drop the leading "<jats:p>" envelope and known boilerplate.
  const stripped = raw
    .replace(/<[^>]+>/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#x?[0-9a-f]+;/gi, '')
    .replace(/\s+/g, ' ')
    .trim();
  return stripped.length > 0 ? stripped : null;
}

interface CrossrefEnvelope {
  status?: string;
  message?: CrossrefMessage;
}

function pickYear(msg: CrossrefMessage): number | null {
  const fromPublished = msg.published?.['date-parts']?.[0]?.[0];
  const fromIssued = msg.issued?.['date-parts']?.[0]?.[0];
  const y = fromPublished ?? fromIssued;
  return typeof y === 'number' ? y : null;
}

function parseWork(msg: CrossrefMessage, fallbackDoi: string): CrossrefWork {
  const retractionDois: string[] = [];
  for (const upd of msg['update-to'] ?? []) {
    const t = upd.type?.toLowerCase() ?? '';
    if (upd.DOI && (t === 'retraction' || t === 'withdrawal' || t === 'removal')) {
      retractionDois.push(upd.DOI);
    }
  }
  // Also: if relation.retraction lists DOIs, treat as retraction.
  const relRetractions = msg.relation?.retraction ?? [];
  for (const r of relRetractions) {
    if (r.id) retractionDois.push(r.id);
  }
  const isRetracted =
    retractionDois.length > 0 ||
    msg.type?.toLowerCase() === 'retraction' ||
    msg.subtype?.toLowerCase() === 'retraction';
  return {
    doi: msg.DOI ?? fallbackDoi,
    title: msg.title?.[0] ?? '',
    authors: (msg.author ?? []).map((a) => ({ given: a.given, family: a.family })),
    containerTitle: msg['container-title']?.[0] ?? null,
    year: pickYear(msg),
    type: msg.type ?? null,
    isRetracted,
    retractionDois,
    abstract: stripAbstractMarkup(msg.abstract),
  };
}

export async function resolveDoi(
  doi: string,
  opts: CrossrefOptions = {},
): Promise<Result<CrossrefWork>> {
  const url = crossrefWorkUrl(doi, opts);
  const res = await fetchJson(url);
  if (!res.ok) return res;
  const env = res.data as CrossrefEnvelope;
  if (env.status !== 'ok' || !env.message) {
    return {
      ok: false,
      degraded: true,
      error: { kind: 'invalid_response', host: 'api.crossref.org', message: 'unexpected Crossref envelope' },
    };
  }
  return { ok: true, data: parseWork(env.message, doi) };
}
